import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDistanceToNow } from 'date-fns';

interface ConditionalSkipEvent {
  id: string;
  bot_id: string;
  signal_type: string;
  action: string;
  instrument: string;
  signal_timestamp: string | null;
  received_at: string;
  decision: string;
  reason: string | null;
  win: number | null;
  response_status: number | null;
  response_ms: number | null;
  is_virtual_forward: boolean;
  next_endpoint: string | null;
  created_at: string;
  matched_pattern_id?: string | null;
  pattern_type?: string;
  pattern_sequence?: string;
  bots?: {
    name: string;
    type: string;
  };
}

export const ConditionalSkipHistory: React.FC = () => {
  const [selectedBotId, setSelectedBotId] = useState<string>('all');
  const [selectedDecision, setSelectedDecision] = useState<string>('all');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('all');
  const isMobile = useIsMobile();

  // Fetch strategy groups for filtering
  const { data: groups = [] } = useQuery({
    queryKey: ['strategy-groups-filter'],
    queryFn: async () => {
      // @ts-ignore - strategy_groups table
      const { data } = await supabase
        // @ts-ignore
        .from('strategy_groups')
        .select('id, name, group_type')
        .eq('is_active', true)
        .order('name');
      return data as Array<{ id: string; name: string; group_type: string }>;
    },
  });

  // Fetch events/logs
  const { 
    data: events = [], 
    isLoading: eventsLoading,
    refetch: refetchEvents 
  } = useQuery({
    queryKey: ['conditional-skip-events', selectedBotId, selectedDecision, selectedGroupId],
    queryFn: async () => {
      // If filtering by group, first get bot IDs in that group
      let botIdsInGroup: string[] | null = null;
      if (selectedGroupId !== 'all') {
        // @ts-ignore - strategy_group_members table
        const { data: members } = await supabase
          // @ts-ignore
          .from('strategy_group_members')
          .select('bot_id')
          .eq('group_id', selectedGroupId)
          .eq('is_active', true);
        
        botIdsInGroup = members?.map(m => m.bot_id) || [];
      }

      let query = supabase
        .from('conditional_skip_events')
        .select(`
          *,
          bots:bot_id (
            name,
            type
          )
        `);

      if (selectedBotId !== 'all') {
        query = query.eq('bot_id', selectedBotId);
      } else if (botIdsInGroup && botIdsInGroup.length > 0) {
        // Filter by group bot IDs
        query = query.in('bot_id', botIdsInGroup);
      } else if (selectedGroupId !== 'all' && (!botIdsInGroup || botIdsInGroup.length === 0)) {
        // Group has no bots, return empty
        return [];
      }

      if (selectedDecision !== 'all') {
        query = query.eq('decision', selectedDecision);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data as ConditionalSkipEvent[];
    },
    refetchInterval: 3000, // Refresh every 3 seconds
  });

  // Fetch available bots for filtering
  const { data: bots = [] } = useQuery({
    queryKey: ['bots-for-history'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bots')
        .select('id, name, type')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('conditional-skip-events-history')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conditional_skip_events'
        },
        () => {
          refetchEvents();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetchEvents]);

  const getDecisionBadgeVariant = (decision: string) => {
    switch (decision) {
      case 'FORWARDED':
        return 'default';
      case 'SKIPPED':
        return 'secondary';
      case 'INFO':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <Card>
      <CardHeader className={cn("flex items-start", isMobile ? "flex-col gap-3" : "flex-row justify-between")}>
        <CardTitle>Signal Processing History</CardTitle>
        <div className={cn("flex gap-2", isMobile ? "flex-col w-full" : "flex-row items-center")}>
          <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
            <SelectTrigger className={isMobile ? "w-full" : "w-[160px]"}>
              <SelectValue placeholder="Filter by group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              {groups.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedBotId} onValueChange={setSelectedBotId}>
            <SelectTrigger className={isMobile ? "w-full" : "w-[180px]"}>
              <SelectValue placeholder="Filter by bot" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bots</SelectItem>
              {bots.map((bot) => (
                <SelectItem key={bot.id} value={bot.id}>
                  {bot.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedDecision} onValueChange={setSelectedDecision}>
            <SelectTrigger className={isMobile ? "w-full" : "w-[140px]"}>
              <SelectValue placeholder="Filter decision" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Decisions</SelectItem>
              <SelectItem value="FORWARDED">Forwarded</SelectItem>
              <SelectItem value="SKIPPED">Skipped</SelectItem>
              <SelectItem value="INFO">Info</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetchEvents()}
            disabled={eventsLoading}
            className={isMobile ? "w-full" : ""}
          >
            <RefreshCw className={`h-4 w-4 ${eventsLoading ? 'animate-spin' : ''}`} />
            {isMobile && <span className="ml-2">Refresh</span>}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {eventsLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : isMobile ? (
          <div className="space-y-3">
            {events.map((event) => (
              <Card key={event.id}>
                <CardContent className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex flex-wrap gap-1">
                      <Badge variant={getDecisionBadgeVariant(event.decision)}>
                        {event.decision}
                      </Badge>
                      {event.matched_pattern_id && (
                        <Badge variant={event.pattern_type === 'lock' ? 'destructive' : 'default'}>
                          {(event.pattern_type || '').toUpperCase()}
                        </Badge>
                      )}
                      {event.signal_type === 'ENTER' && 
                       event.decision === 'SKIPPED' && 
                       event.reason?.toLowerCase().includes('not active in group') && (
                        <Badge variant="outline" className="border-orange-500 text-orange-500">
                          ⚠️ Non-Active
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                      {formatTimestamp(event.created_at)}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Bot</p>
                      <p className="font-medium">{event.bots?.name || 'Unknown'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Signal</p>
                      <p className="font-medium">{event.action}</p>
                      <p className="text-muted-foreground text-xs">{event.instrument}</p>
                    </div>
                    {event.pattern_sequence && (
                      <div>
                        <p className="text-muted-foreground text-xs">Pattern</p>
                        <div className="text-xs font-mono bg-muted px-2 py-1 rounded">
                          {event.pattern_sequence}
                        </div>
                      </div>
                    )}
                    {event.reason && (
                      <div>
                        <p className="text-muted-foreground text-xs">Reason</p>
                        <p className="text-xs">{event.reason}</p>
                      </div>
                    )}
                    {event.response_status && (
                      <div className="flex flex-wrap gap-2">
                        <Badge variant={event.response_status === 200 ? 'default' : 'destructive'}>
                          {event.response_status}
                        </Badge>
                        {event.is_virtual_forward && (
                          <Badge variant="outline" className="text-orange-500 border-orange-500">
                            🔄 Virtual
                          </Badge>
                        )}
                        {event.response_ms && (
                          <span className="text-xs text-muted-foreground">{event.response_ms}ms</span>
                        )}
                        {event.win !== null && (
                          <Badge variant={event.win === 1 ? 'default' : 'destructive'}>
                            {event.win === 1 ? 'WIN' : 'LOSS'}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Bot</TableHead>
                <TableHead>Signal</TableHead>
                <TableHead>Decision</TableHead>
                <TableHead>Pattern Match</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Response</TableHead>
                <TableHead>Win</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="text-sm">
                    {formatTimestamp(event.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">{event.bots?.name || 'Unknown'}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">{event.action}</div>
                      <div className="text-muted-foreground">{event.instrument}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant={getDecisionBadgeVariant(event.decision)}>
                        {event.decision}
                      </Badge>
                      {event.signal_type === 'ENTER' && 
                       event.decision === 'SKIPPED' && 
                       event.reason?.toLowerCase().includes('not active in group') && (
                        <Badge variant="outline" className="border-orange-500 text-orange-500">
                          ⚠️ Non-Active ENTER
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {event.matched_pattern_id && (
                      <div className="space-y-1">
                        <Badge variant={event.pattern_type === 'lock' ? 'destructive' : 'default'}>
                          {(event.pattern_type || '').toUpperCase()}
                        </Badge>
                        {event.pattern_sequence && (
                          <div className="text-xs font-mono bg-muted px-1 py-0.5 rounded">
                            {event.pattern_sequence}
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">
                    {event.reason}
                  </TableCell>
                  <TableCell>
                    {event.response_status && (
                      <div className="text-sm space-y-1">
                        <div className="flex items-center gap-1">
                          <Badge variant={event.response_status === 200 ? 'default' : 'destructive'}>
                            {event.response_status}
                          </Badge>
                          {event.is_virtual_forward && (
                            <Badge variant="outline" className="text-orange-500 border-orange-500">
                              🔄 Virtual
                            </Badge>
                          )}
                        </div>
                        {event.response_ms && (
                          <div className="text-muted-foreground">{event.response_ms}ms</div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {event.win !== null && (
                      <Badge variant={event.win === 1 ? 'default' : 'destructive'}>
                        {event.win === 1 ? 'WIN' : 'LOSS'}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        
        {!eventsLoading && events.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No events found. Check your filters or wait for signals to be processed.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
